import { Image } from 'core/common/ui/ui-kit';
import { isValidImageSrc } from 'core/common/utils/isValidImageSrc';
import { ScreenAttributes } from 'core/flow/entities';

type ImagesProps = {
  attributes: ScreenAttributes[];
};

type PreloadImage = {
  src: string;
  // A screen background is a CSS value the browser fetches at its origin url, bypassing the optimizer
  unoptimized: boolean;
};

const imageSrcPattern = /\.(png|jpe?g|webp|svg|gif)(\?.*)?$/i;
const cssUrlPattern = /url\((['"]?)(.+?)\1\)/g;

const getImageKey = ({ src, unoptimized }: PreloadImage) => `${src}:${unoptimized}`;

const isImageSrc = (value: unknown): value is string =>
  typeof value === 'string' && isValidImageSrc(value) && imageSrcPattern.test(value);

const collectImageSrcs = (value: unknown, srcs: Set<string>) => {
  if (isImageSrc(value)) {
    srcs.add(value);
    return;
  }

  if (typeof value !== 'object' || value === null) return;

  Object.values(value).forEach((nestedValue) => collectImageSrcs(nestedValue, srcs));
};

const getBackgroundImages = (background: string | undefined): PreloadImage[] =>
  [...(background ?? '').matchAll(cssUrlPattern)].map(([, , src]) => ({
    src,
    unoptimized: true,
  }));

const getContentImages = (attributes: Omit<ScreenAttributes, 'background'>): PreloadImage[] => {
  const srcs = new Set<string>();

  collectImageSrcs(attributes, srcs);

  return [...srcs].map((src) => ({ src, unoptimized: false }));
};

const getScreenImages = ({ background, ...content }: ScreenAttributes) => [
  ...getContentImages(content),
  ...getBackgroundImages(background),
];

const getUniqueImages = (attributes: ScreenAttributes[]) => {
  const imagesByKey = new Map(
    attributes.flatMap(getScreenImages).map((image) => [getImageKey(image), image]),
  );

  return [...imagesByKey.values()];
};

export const Images = ({ attributes }: ImagesProps) => {
  const images = getUniqueImages(attributes);

  return (
    <>
      {images.map((image) => (
        <Image
          fill
          key={getImageKey(image)}
          src={image.src}
          alt=""
          loading="eager"
          fetchPriority="low"
          unoptimized={image.unoptimized}
        />
      ))}
    </>
  );
};
